import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save, Loader2, Upload, Music, Star, CheckCircle2, Volume2, Play, Pause } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GENRES = ["Exclusive", "Beat", "Single", "Remix"];
const EMPTY = { title: "", artist: "", artist_handle: "", artwork: "", audio_url: "", external_url: "", genre: "Exclusive", duration: "", featured: false, status: "published" };

function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

const VOICES = [
  { id: "onyx", label: "Onyx — deep male" },
  { id: "echo", label: "Echo — smooth male" },
  { id: "nova", label: "Nova — energetic female" },
  { id: "shimmer", label: "Shimmer — bright female" },
  { id: "coral", label: "Coral — warm female" },
  { id: "sage", label: "Sage — measured" },
  { id: "alloy", label: "Alloy — neutral" },
];

function StationVoiceover() {
  const [settings, setSettings] = useState(null);
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("onyx");
  const [model, setModel] = useState("tts-1-hd");
  const [previewUrl, setPreviewUrl] = useState("");
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const audioRef = useRef(null);
  const bedRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [bedUrl, setBedUrl] = useState("");
  const [bedVolume, setBedVolume] = useState(0.18);
  const [bedUploading, setBedUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/radio/settings`);
      setSettings(data);
      setText(data.station_id_text || "");
      setVoice(data.station_id_voice || "onyx");
      setPreviewUrl(data.station_id_url || "");
      setBedUrl(data.station_id_bed_url || "");
      setBedVolume(typeof data.station_id_bed_volume === "number" ? data.station_id_bed_volume : 0.18);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadBed = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBedUploading(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBedUrl(data.path);
      setMsg("done:Beat uploaded. Adjust the level, Preview, then Save.");
    } catch { setMsg("err:Beat upload failed."); }
    finally { setBedUploading(false); e.target.value = ""; }
  };

  const generate = async () => {
    setMsg(""); setGen(true);
    try {
      const { data } = await axios.post(`${API}/admin/radio/voiceover`, { text, voice, model });
      setPreviewUrl(data.url);
      setMsg("done:Voiceover generated. Preview it, then Save as station intro.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg(`err:${typeof d === "string" ? d : "Generation failed. Try again."}`);
    } finally { setGen(false); }
  };

  const save = async (enabled) => {
    setSaving(true); setMsg("");
    try {
      const { data } = await axios.post(`${API}/admin/radio/settings`, {
        station_id_url: previewUrl, station_id_text: text, station_id_voice: voice, station_id_enabled: enabled,
        station_id_bed_url: bedUrl, station_id_bed_volume: bedVolume,
      });
      setSettings(data);
      setMsg(`done:${enabled ? "Station intro is LIVE — it plays first when listeners hit Play The Station." : "Saved. Station intro is OFF."}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg(`err:${typeof d === "string" ? d : "Save failed."}`);
    } finally { setSaving(false); }
  };

  const togglePreview = () => {
    const a = audioRef.current;
    const b = bedRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
      if (b && bedUrl) { b.volume = bedVolume; b.currentTime = 0; b.play().catch(() => {}); }
    } else {
      a.pause();
      if (b) b.pause();
    }
  };

  const inputCls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
  const enabled = !!settings?.station_id_enabled;

  return (
    <div className="border border-white/10 p-5 md:p-6 mb-8" data-testid="station-voiceover">
      <div className="flex items-center gap-2 mb-1">
        <Volume2 size={16} className="text-[#E30613]" />
        <h3 className="font-display uppercase text-xl tracking-tight">Station Voiceover</h3>
        <span className={`ml-auto text-[10px] uppercase tracking-[0.2em] font-bold ${enabled ? "text-[#E30613]" : "text-neutral-600"}`} data-testid="station-voiceover-state">{enabled ? "Intro ON" : "Intro OFF"}</span>
      </div>
      <p className="text-sm text-neutral-500 mb-4">Generate an AI voiceover station ID. When ON, it plays first when a listener hits "Play The Station".</p>

      <textarea data-testid="voiceover-text" value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="You're listening to VLMAG Radio..." className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm mb-3" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Voice</p>
          <select data-testid="voiceover-voice" value={voice} onChange={(e) => setVoice(e.target.value)} className={`${inputCls} w-full px-3`}>
            {VOICES.map((v) => <option key={v.id} value={v.id} className="bg-black">{v.label}</option>)}
          </select>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Quality</p>
          <div className="flex gap-2">
            {[["tts-1-hd", "HD"], ["tts-1", "Standard"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setModel(k)} className={`flex-1 h-12 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${model === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={generate} disabled={gen || !text.trim()} data-testid="voiceover-generate" className="inline-flex items-center gap-2 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors disabled:opacity-50">
          {gen ? <Loader2 className="animate-spin" size={14} /> : <Volume2 size={14} />} {previewUrl ? "Re-Generate" : "Generate Voiceover"}
        </button>
        {previewUrl && (
          <button onClick={togglePreview} data-testid="voiceover-preview" className="inline-flex items-center gap-2 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
            {playing ? <Pause size={14} /> : <Play size={14} />} Preview
          </button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Background Beat (optional)</p>
        <p className="text-xs text-neutral-600 mb-3">Upload a short instrumental/loop to play softly under the voice ID. It fades out as the first song starts.</p>
        <div className="flex flex-wrap items-center gap-3">
          <label data-testid="bed-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 h-11 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
            {bedUploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {bedUrl ? "Replace Beat" : "Upload Beat"}
            <input type="file" accept="audio/*" className="hidden" onChange={uploadBed} />
          </label>
          {bedUrl && (
            <>
              <button type="button" onClick={() => setBedUrl("")} data-testid="bed-remove" className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-[#E30613] transition-colors">Remove</button>
              <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 shrink-0">Level</span>
                <input type="range" min="0.05" max="0.5" step="0.01" value={bedVolume} onChange={(e) => setBedVolume(parseFloat(e.target.value))} data-testid="bed-volume" className="flex-1 accent-[#E30613]" />
                <span className="text-[10px] font-bold text-neutral-400 w-10 text-right">{Math.round(bedVolume * 100)}%</span>
              </div>
            </>
          )}
        </div>
      </div>

      {previewUrl && (
        <audio ref={audioRef} src={mediaUrl(previewUrl)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); if (bedRef.current) bedRef.current.pause(); }} className="hidden" />
      )}
      {bedUrl && <audio ref={bedRef} src={mediaUrl(bedUrl)} loop className="hidden" />}

      {msg && <p className={`text-sm mt-3 ${msg.startsWith("err") ? "text-[#E30613]" : "text-emerald-400"}`} data-testid="voiceover-msg">{msg.slice(msg.indexOf(":") + 1)}</p>}

      {previewUrl && (
        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
          <button onClick={() => save(true)} disabled={saving} data-testid="voiceover-set-intro" className="inline-flex items-center gap-2 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Set As Station Intro
          </button>
          {enabled && (
            <button onClick={() => save(false)} disabled={saving} data-testid="voiceover-disable" className="inline-flex items-center gap-2 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors disabled:opacity-60">
              Turn Intro Off
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function RadioManager({ role }) {
  const isAdmin = (role || "admin") === "admin";
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/radio/tracks`); setTracks(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm({ ...EMPTY, status: isAdmin ? "published" : "draft" }); setEditingId(null); setError(""); setView("edit"); };
  const openEdit = (t) => { setForm({ ...EMPTY, ...t }); setEditingId(t.id); setError(""); setView("edit"); };

  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(kind); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [kind === "audio" ? "audio_url" : "artwork"]: data.path }));
    } catch { setError(`${kind === "audio" ? "Audio" : "Artwork"} upload failed.`); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const save = async () => {
    setError("");
    if (!form.title.trim() || !form.artist.trim()) { setError("Track title and artist are required."); return; }
    if (!form.audio_url && !form.external_url) { setError("Upload an audio file or add an external link."); return; }
    setSaving(true);
    try {
      const payload = { ...form, featured: !!form.featured };
      if (editingId) await axios.put(`${API}/radio/tracks/${editingId}`, payload);
      else await axios.post(`${API}/radio/tracks`, payload);
      await load(); setView("list");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    await axios.delete(`${API}/radio/tracks/${t.id}`); await load();
  };
  const approve = async (t) => {
    try { await axios.put(`${API}/radio/tracks/${t.id}`, { ...t, status: "published" }); await load(); }
    catch (err) { alert(err?.response?.data?.detail || "Publish failed."); }
  };

  const inputCls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";

  if (view === "edit") {
    return (
      <div className="max-w-3xl" data-testid="radio-editor">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> Back to tracks</button>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-6">{editingId ? "Edit Track" : "New Track"}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Track title</p><Input data-testid="radio-f-title" value={form.title} onChange={set("title")} className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Artist / Producer</p><Input data-testid="radio-f-artist" value={form.artist} onChange={set("artist")} className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Instagram @handle</p><Input data-testid="radio-f-ig" value={form.artist_handle} onChange={set("artist_handle")} placeholder="@artist" className={inputCls} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Genre</p>
              <div className="flex flex-wrap gap-2">{GENRES.map((g) => (<button key={g} type="button" onClick={() => setForm((f) => ({ ...f, genre: g }))} className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border transition-colors ${form.genre === g ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>{g}</button>))}</div>
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Audio file (MP3 / WAV) — powers the in-site player</p>
            <div className="flex gap-2 items-center">
              <label data-testid="radio-audio-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                {uploading === "audio" ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} {form.audio_url ? "Replace audio" : "Upload audio"}
                <input type="file" accept="audio/*" className="hidden" onChange={upload("audio")} />
              </label>
              {form.audio_url && <span className="inline-flex items-center gap-2 text-xs text-emerald-400"><Music size={14} /> Audio attached</span>}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">External link (optional — SoundCloud / Spotify / YouTube "listen on")</p>
            <Input data-testid="radio-f-external" value={form.external_url} onChange={set("external_url")} placeholder="https://soundcloud.com/..." className={inputCls} />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Cover artwork</p>
            <div className="flex gap-2 items-center">
              <Input data-testid="radio-f-artwork" value={form.artwork} onChange={set("artwork")} placeholder="https://... or upload" className={inputCls} />
              <label className="cursor-pointer shrink-0 inline-flex items-center gap-2 px-4 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                {uploading === "art" ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={upload("art")} />
              </label>
            </div>
            {form.artwork && <img src={mediaUrl(form.artwork)} alt="" className="mt-3 w-32 h-32 object-cover grayscale-img border border-white/10" />}
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="radio-f-featured">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-[#E30613]" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-neutral-300 inline-flex items-center gap-2"><Star size={13} className="text-[#E30613]" /> Feature as Artist Spotlight</span>
          </label>

          <div data-testid="radio-f-status">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Status</p>
            <div className="flex gap-2">
              {[["draft", "Draft"], ["published", "Published"]].map(([k, l]) => {
                const locked = k === "published" && !isAdmin;
                return <button key={k} type="button" disabled={locked} onClick={() => !locked && setForm((f) => ({ ...f, status: k }))} className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-40 ${form.status === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>{l}</button>;
              })}
            </div>
            {!isAdmin && <p className="text-[10px] text-neutral-600 mt-2">Editors can save drafts. Only admins publish to the live station.</p>}
          </div>

          {error && <p className="text-[#E30613] text-sm" data-testid="radio-f-error">{error}</p>}
          <button onClick={save} disabled={saving} data-testid="radio-f-save" className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editingId ? "Update Track" : "Add To Station"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="radio-manager">
      {isAdmin && <StationVoiceover />}
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-neutral-400 text-sm">{tracks.length} track(s) · {tracks.filter((t) => t.published !== false).length} live</p>
        <button onClick={openNew} data-testid="radio-new" className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors"><Plus size={15} /> New Track</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : tracks.length === 0 ? (
        <p className="text-neutral-600 py-16 text-center border border-white/10">No tracks yet. Add the first drop.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="radio-track-rows">
          {tracks.map((t, i) => (
            <div key={t.id} className="flex items-center gap-4 p-3" data-testid={`radio-row-${i}`}>
              <div className="w-12 h-12 shrink-0 border border-white/10 overflow-hidden bg-neutral-900">{t.artwork && <img src={mediaUrl(t.artwork)} alt="" className="w-full h-full object-cover grayscale-img" />}</div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold truncate">{t.title} {t.featured && <Star size={12} className="inline text-[#E30613]" fill="#E30613" />}</p>
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 truncate">{t.artist} · {t.genre}{t.audio_url ? "" : " · link only"}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] font-bold shrink-0 ${t.status === "pending" ? "text-amber-400" : t.published !== false ? "text-[#E30613]" : "text-neutral-600"}`}>{t.status === "pending" ? "Pending" : t.published !== false ? "Live" : "Draft"}</span>
              <div className="flex items-center gap-3 shrink-0">
                {t.status === "pending" && <button onClick={() => approve(t)} data-testid={`radio-approve-${i}`} title="Publish" className="text-emerald-400 hover:text-emerald-300"><CheckCircle2 size={16} /></button>}
                <button onClick={() => openEdit(t)} data-testid={`radio-edit-${i}`} title="Edit" className="text-neutral-500 hover:text-white"><Pencil size={16} /></button>
                <button onClick={() => remove(t)} data-testid={`radio-delete-${i}`} title="Delete" className="text-neutral-500 hover:text-[#E30613]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
